import { updateBy } from '@toggle/helpers';
import { t } from 'i18next';

import { create } from '~/utils/store/createStore';

import { uploadFiles } from '../services/user-document-service';
import { allowedTypes } from '../utils/formatUploadFiles';
import {
  UploadedFile,
  UploadingStatus,
  UserUploadStore,
} from './use-user-upload.types';

const MAX_FILE_SIZE = 7 * 1024 * 1024; // 7MB in bytes
const CHUNK_SIZE = 5;

export const useUserUpload = create<UserUploadStore>((set, get) => ({
  files: [],
  alertOpen: false,
  toggleAlertOpen: (open: boolean) => set({ alertOpen: open }),
  addFiles: (newFiles: UploadedFile[]) => {
    set(state => ({
      files: [...state.files, ...newFiles],
    }));

    get().uploadFiles(newFiles);
  },
  uploadFiles: async (files: UploadedFile[]) => {
    const { updateFile, toggleAlertOpen } = get();

    toggleAlertOpen(false);

    const invalidFiles = files.filter(
      file =>
        !allowedTypes.includes(file.fileType) ||
        file.sizeInBytes > MAX_FILE_SIZE
    );
    const validFiles = files.filter(
      file =>
        allowedTypes.includes(file.fileType) &&
        file.sizeInBytes <= MAX_FILE_SIZE
    );

    if (invalidFiles.length) {
      toggleAlertOpen(true);

      const invalidTypeFilesWithError = invalidFiles.map(file => ({
        ...file,
        status: 'error' as UploadingStatus,
        errorMessage: !allowedTypes.includes(file.fileType)
          ? t('chat:userUpload.invalidFileType')
          : t('chat:userUpload.fileSizeExceedsLimit'),
      }));

      invalidTypeFilesWithError.forEach(file => {
        updateFile(file);
      });
    }

    for (let i = 0; i < validFiles.length; i += CHUNK_SIZE) {
      const chunk = validFiles.slice(i, i + CHUNK_SIZE);

      const { data: response, error } = await uploadFiles(chunk);

      const resultMap = new Map(
        response?.results.map(r => [r.field_name, r]) || []
      );

      chunk.forEach(file => {
        const result = resultMap.get(file.field_name);
        const hasError = error || !result?.success;
        const errorMessage =
          error?.message ||
          result?.error ||
          t('chat:userUpload.failedToUpload');

        const updatedFile: UploadedFile = {
          ...file,
          id: result?.id || file.id,
          status: hasError ? 'error' : 'complete',
          errorMessage: hasError ? errorMessage : undefined,
        };

        updateFile(updatedFile);

        if (hasError) {
          toggleAlertOpen(true);
        }
      });
    }
  },
  removeFile: (field_name: string) => {
    // clean up the thumbnail url to prevent memory leak
    const file = get().files.find(f => f.field_name === field_name);

    if (file?.thumbnailUrl?.startsWith('blob:')) {
      URL.revokeObjectURL(file.thumbnailUrl);
    }

    set(state => ({
      files: state.files.filter(file => file.field_name !== field_name),
    }));
  },
  updateFile: (file: UploadedFile) => {
    set(state => ({
      files: updateBy(
        state.files,
        { field_name: file.field_name },
        existingFile => ({
          ...existingFile,
          ...file,
        })
      ),
    }));
  },
  removeAllFiles: () => {
    // clean up the thumbnail urls to prevent memory leak
    get().files.forEach(file => {
      if (file.thumbnailUrl?.startsWith('blob:')) {
        URL.revokeObjectURL(file.thumbnailUrl);
      }
    });

    set({ files: [] });
  },
}));
